<template>
    <div class="announcement-show loading-content-container">
        <div v-if="isLoading" class="loading-overlay d-flex justify-content-center pt-5">
            <csp-loading-indicator/>
        </div>

        <b-row v-if="!isLoading">
            <csp-desktop-info-card
                :cols="12"
                :form-context="initialFormContexts.announcement"
                :show-buttons="false"
                title="Ankündigung"
                @save="saveAnnouncement">
                <b-row>
                    <csp-desktop-info-card-element
                        class="col-12"
                        :form-context="initialFormContexts.announcement"
                        label="Inhalt"
                        property="announcement"
                        required
                        :show-label="false"
                        type="ckeditorReduced"/>
                </b-row>
                <b-row>
                    <csp-desktop-info-card-element
                        class="col-2"
                        :form-context="initialFormContexts.announcement"
                        label="Gültig ab"
                        :max-date="null"
                        property="dateBegin"
                        type="datepicker"/>

                    <csp-desktop-info-card-element
                        class="col-2"
                        :form-context="initialFormContexts.announcement"
                        label="Gültig bis"
                        :max-date="null"
                        property="dateEnd"
                        type="datepicker"/>

                    <csp-desktop-info-card-element
                        class="col-2"
                        :form-context="initialFormContexts.announcement"
                        label="Wie oft anzeigen? (0 = unbegrenzt)"
                        property="maxOccurrence"
                        type="number"/>

                    <csp-desktop-info-card-element
                        class="col-4"
                        :form-context="initialFormContexts.announcement"
                        label="Zielgruppe"
                        property="roleGroup"
                        type="checkboxGroup"/>

                    <csp-desktop-info-card-element
                        class="col-2"
                        :form-context="initialFormContexts.announcement"
                        label="Gebiet"
                        property="area"
                        type="autocomplete"/>
                </b-row>

                <b-row>
                    <csp-desktop-info-card-element
                        class="col-3"
                        :form-context="initialFormContexts.announcement"
                        label="Firma"
                        :options-loader="loadFirmOptions"
                        property="firm"
                        type="autocomplete"/>

                    <csp-desktop-info-card-element
                        class="col-3"
                        :form-context="initialFormContexts.announcement"
                        label="Firmengruppe"
                        :options-loader="loadFirmGroupOptions"
                        property="firmGroup"
                        type="autocomplete"/>

                    <csp-desktop-info-card-element
                        class="col-3"
                        :form-context="initialFormContexts.announcement"
                        label="Zuordnung"
                        :options-loader="loadFirmAssociationOptions"
                        property="firmAssociation"
                        type="autocomplete"/>
                </b-row>

                <b-row v-if="existingFileId && !initialFormContexts.announcement.isInEditMode" class="mt-3">
                    <b-col cols="12">
                        <label class="font-weight-bold">Bild</label>
                        <p class="text-muted mb-2">
                            Platzhalter für die Bildposition im Text:
                            <code v-pre>{{ ankuendigung.bild }}</code>
                        </p>
                        <b-img
                            class="announcement-image-preview"
                            fluid
                            :src="`/api/desktop/announcements/${announcementId}/file`"/>
                    </b-col>
                </b-row>

                <b-row v-if="initialFormContexts.announcement.isInEditMode" class="mt-3">
                    <b-col cols="12">
                        <label class="font-weight-bold">Bild</label>
                        <p class="text-muted mb-2">
                            Platzhalter für die Bildposition im Text:
                            <code v-pre>{{ ankuendigung.bild }}</code>
                            <a
                                v-if="visibleFileId"
                                class="ml-2"
                                title="Bild entfernen"
                                @click.prevent="removeFile">
                                <i class="inticon-autoclaim_delete" style="font-size: 1.5rem;"/>
                            </a>
                        </p>
                        <b-img v-if="visibleFileId"
                            class="announcement-image-preview"
                            fluid
                            :src="visibleFilePreviewUrl"/>
                        <csp-base-file-upload
                            v-if="!visibleFileId"
                            accepted-mime-types="image/jpeg,image/png"
                            :additional-params="fileUploadParams"
                            :max-files="1"
                            upload-url="/api/desktop/announcements/files"
                            @upload-complete="handleFileUploadComplete"/>
                    </b-col>
                </b-row>

                <template>
                    <b-row class="mt-3">
                        <b-col cols="6"/>
                        <b-col class="text-right" cols="6">
                            <b-button
                                v-if="showPreviewButton"
                                class="mr-2"
                                :disabled="isLoadingPreview"
                                variant="primary"
                                @click="openPreview">
                                Vorschau
                            </b-button>
                            <b-button
                                v-if="showEditButton"
                                class="mr-2"
                                variant="primary"
                                @click="$router.push({query: {editMode: 'announcement'}})">
                                Bearbeiten
                            </b-button>
                            <b-button
                                v-if="showDeleteButton"
                                variant="secondary"
                                @click="deleteAnnouncement">
                                Löschen
                            </b-button>
                            <b-button
                                v-if="initialFormContexts.announcement.isInEditMode"
                                class="mr-2"
                                variant="secondary"
                                @click="cancelAnnouncement">
                                Abbrechen
                            </b-button>
                            <b-button
                                v-if="initialFormContexts.announcement.isInEditMode"
                                variant="primary"
                                @click="saveAnnouncement(initialFormContexts.announcement)">
                                Speichern
                            </b-button>
                        </b-col>
                    </b-row>
                </template>
            </csp-desktop-info-card>
        </b-row>

        <csp-announcements
            v-if="isPreviewModalOpen"
            acknowledge-url=""
            :announcement-html="previewHtml"
            :announcement-ids="[]"
            :show-confirm="false"
            @hidden="isPreviewModalOpen = false"/>
    </div>
</template>

<script>
    import FormContext from '@desktop/components/FormContext';
    import FormDataMixin from '@desktop/components/mixins/FormDataMixin';
    import Container from '@services/Container';
    import {generateUploadSessionId} from '@js/functions/session';

    const container = Container.getInstance();
    const repository = container.getDesktopAnnouncementRepository();
    const flashMessenger = container.getFlashMessenger();

    export default {
        name: 'AnnouncementDetail',
        mixins: [FormDataMixin],
        data() {
            return {
                isLoading: true,
                isPreviewModalOpen: false,
                isLoadingPreview: false,
                previewHtml: null,
                uploadSessionId: generateUploadSessionId(),
                uploadedFileId: null,
                removeFileFlag: false,
                initialFormContexts: {
                    announcement: new FormContext({
                        name: 'announcement',
                    }),
                },
            };
        },
        computed: {
            announcementId() {
                return Number(this.$route.params.announcementId);
            },
            existingFileId() {
                return this.initialFormContexts.announcement.data?.fileId ?? null;
            },
            fileUploadParams() {
                return {uploadSessionId: this.uploadSessionId};
            },
            showDeleteButton() {
                const ctx = this.initialFormContexts.announcement;
                return !ctx.isInEditMode && ctx.permissions.canDelete === true;
            },
            showEditButton() {
                const ctx = this.initialFormContexts.announcement;
                return !ctx.isInEditMode && ctx.permissions.canEdit === true;
            },
            visibleFileId() {
                if (this.removeFileFlag) {
                    return null;
                }
                return this.uploadedFileId ?? this.existingFileId;
            },
            visibleFilePreviewUrl() {
                if (this.uploadedFileId) {
                    return `/api/desktop/announcements/files/${this.uploadSessionId}/${this.uploadedFileId}`;
                }
                if (this.existingFileId) {
                    return `/api/desktop/announcements/${this.announcementId}/file`;
                }
                return null;
            },
            showPreviewButton() {
                const ctx = this.initialFormContexts.announcement;
                const html = ctx.isInEditMode ? ctx.form.getData().announcement : ctx.data.announcement;
                return !!html;
            },
        },
        watch: {
            'initialFormContexts.announcement.form.data.firm'(newValue) {
                if (!newValue) {
                    return;
                }
                // reset other values upon value selection, only one of the three can be set
                this.initialFormContexts.announcement.form.setProperty('firmGroup', null);
                this.initialFormContexts.announcement.form.setProperty('firmAssociation', null);
            },
            'initialFormContexts.announcement.form.data.firmGroup'(newValue) {
                if (!newValue) {
                    return;
                }
                // reset other values upon value selection, only one of the three can be set
                this.initialFormContexts.announcement.form.setProperty('firm', null);
                this.initialFormContexts.announcement.form.setProperty('firmAssociation', null);
            },
            'initialFormContexts.announcement.form.data.firmAssociation'(newValue) {
                if (!newValue) {
                    return;
                }
                // reset other values upon value selection, only one of the three can be set
                this.initialFormContexts.announcement.form.setProperty('firm', null);
                this.initialFormContexts.announcement.form.setProperty('firmGroup', null);
            },
        },
        created() {
            this.loadFormData(() => repository.show(this.announcementId))
                .finally(() => {
                    this.isLoading = false;
                });
        },
        methods: {
            cancelAnnouncement() {
                const query = {...this.$route.query};
                delete query.editMode;
                this.initialFormContexts.announcement.form.reset();
                this.uploadedFileId = null;
                this.removeFileFlag = false;
                this.$router.push({query});
            },
            deleteAnnouncement() {
                if (!confirm('Wollen Sie die Ankündigung wirklich löschen?')) {
                    return;
                }
                repository.delete(this.announcementId).then(
                    () => {
                        flashMessenger.addSuccess('Ankündigung wurde gelöscht');
                        this.$router.push('/de/Schaden-Manager/Dokument/announcements');
                    },
                    () => {
                        flashMessenger.addError('Ankündigung konnte nicht gelöscht werden');
                    }
                );
            },
            handleFileUploadComplete(data) {
                const response = JSON.parse(data.xhr.response);
                this.uploadedFileId = response?.data?.attributes?.fileId ?? null;
                this.removeFileFlag = false;
            },
            loadFirmAssociationOptions() {
                return repository.loadFirmAssociationOptions();
            },
            loadFirmGroupOptions() {
                return repository.loadFirmGroupOptions();
            },
            loadFirmOptions() {
                return repository.loadFirmOptions();
            },
            openPreview() {
                const ctx = this.initialFormContexts.announcement;
                const announcementHtml = ctx.isInEditMode
                    ? ctx.form.getData().announcement
                    : ctx.data.announcement;

                this.isLoadingPreview = true;
                repository.preview({
                    announcementHtml,
                    announcementId: this.announcementId,
                    uploadSessionId: ctx.isInEditMode ? this.uploadSessionId : undefined,
                }).then((response) => {
                    this.previewHtml = response.data.attributes.resolvedHtml;
                    this.isPreviewModalOpen = true;
                }).finally(() => {
                    this.isLoadingPreview = false;
                });
            },
            removeFile() {
                this.uploadedFileId = null;
                this.removeFileFlag = true;
            },
            saveAnnouncement(formContext) {
                this.saveFormData(
                    formContext,
                    (id, postData) => {
                        postData.uploadSessionId = this.uploadSessionId;
                        if (this.removeFileFlag) {
                            postData.removeFile = true;
                        }
                        return repository.saveBasedata(this.announcementId, postData);
                    },
                    null
                ).then(() => {
                    this.uploadedFileId = null;
                    this.removeFileFlag = false;
                    this.uploadSessionId = generateUploadSessionId();
                });
            },
        },
    };
</script>
